import { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { aboutAPI } from '../services/api';
import { useSiteSettings } from '../hooks/useContent';
import { usePageSeo } from '../hooks/usePageSeo';
import { buildBreadcrumbSchema, buildOrganizationSchema } from '../utils/seo';
import Breadcrumbs from '../components/Breadcrumbs';
import LoadingIndicator from '../components/LoadingIndicator';
import './EventDetail.css';

const splitGallery = (gallery) => {
  if (!gallery) return [];
  return gallery
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
};

export default function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { settings } = useSiteSettings();

  const publicBase =
    String(settings.site_public_url || '').replace(/\/$/, '') ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  const brand = settings.site_brand_name || 'Invincible NGO';

  const projectSchema = useMemo(() => {
    if (!project) return null;
    const org = buildOrganizationSchema(settings);
    const breadcrumbs = buildBreadcrumbSchema([
      { name: 'About Us', url: '/about' },
      { name: project.title || 'Project', url: `/projects/${id}` }
    ], publicBase);
    return {
      '@context': 'https://schema.org',
      '@graph': [org, breadcrumbs]
    };
  }, [project, settings, publicBase, id]);

  usePageSeo({
    enabled: Boolean(project),
    title: project?.title ? `${project.title} | ${brand}` : `Project | ${brand}`,
    description: project?.detail || project?.description || settings.site_meta_description,
    canonical: id && publicBase ? `${publicBase}/projects/${id}` : undefined,
    image: project?.imageUrl || undefined,
    schema: projectSchema
  });


  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await aboutAPI.getProjectById(id);
        if (!cancelled) setProject(res.data?.data || null);
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.message || 'Failed to load project.');
          setProject(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="event-detail-loading">
        <LoadingIndicator label="Loading project..." />
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="event-detail-empty">
        <h2>Project Not Found</h2>
        <p>{error || 'The project you are looking for does not exist.'}</p>
        <Link to="/about" className="event-detail-btn-primary">Back To About Us</Link>
      </div>
    );
  }

  const galleryImages = splitGallery(project.gallery);
  const description = project.detail || project.description || '';

  const stats = [
    { label: project.stat1Label, value: project.stat1Value },
    { label: project.stat2Label, value: project.stat2Value },
    { label: project.stat3Label, value: project.stat3Value }
  ].filter((s) => s.value);

  return (
    <div className="event-detail-page">
      <div className="event-detail-topbar">
        <Breadcrumbs
          items={[
            { label: 'About Us', path: '/about' },
            { label: project.title || 'Project' }
          ]}
          className="project-breadcrumbs"
        />
        <Link to="/about" className="event-detail-back">
          ← Back To About Us
        </Link>
      </div>

      <div className="event-detail-cards-full" style={{ marginTop: '1rem' }}>
        <div className="event-detail-card">
          <h1 className="event-detail-title" style={{ marginBottom: '0.75rem' }}>
            {project.title}
          </h1>
          {description && (
            <p
              style={{
                fontSize: '0.98rem',
                lineHeight: 1.7,
                color: '#475569',
                marginBottom: stats.length ? '1.25rem' : '0.75rem'
              }}
            >
              {description}
              {project.link && (
                <>
                  {' '}
                  <a
                    href={project.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--primary-color)', fontWeight: 600 }}
                  >
                    Know more ↗
                  </a>
                </>
              )}
            </p>
          )}

          {stats.length > 0 && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '1.75rem',
                fontSize: '1rem',
                fontWeight: 700,
                color: '#0f172a'
              }}
            >
              {stats.map((s, idx) => (
                <div key={idx}>
                  <span style={{ color: 'var(--primary-color)' }}>{s.value}</span>
                  {s.label ? (
                    <span style={{ color: '#475569', fontWeight: 600 }}> {s.label}</span>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {galleryImages.length > 0 && (
          <div className="event-detail-card" style={{ padding: 0 }}>
            <div className="ed-hero-container" style={{ paddingTop: '1.5rem', paddingBottom: '1.5rem' }}>
              <h2 className="event-detail-section-title" style={{ marginLeft: '0.5rem', marginBottom: '1rem' }}>
                Project Gallery
              </h2>
              <div className="ed-hero-asymmetric">
                <div className="ed-hero-main">
                  {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                  <img src={galleryImages[0]} alt="Project main image"  loading="lazy" decoding="async" />
                </div>
                {galleryImages.length > 1 && (
                  <div className="ed-hero-side">
                    {galleryImages.slice(1, 5).map((url, idx) => (
                      <div key={idx} className="ed-hero-sub">
                        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
                        <img src={url} alt={`Project image ${idx + 2}`}  loading="lazy" decoding="async" />
                      </div>
                    ))}
                    {Array.from({ length: Math.max(0, 4 - galleryImages.slice(1, 5).length) }).map((_, idx) => (
                      <div key={`empty-${idx}`} className="ed-hero-sub empty-bg" />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

